// Payout schedule management (Org 1 owners + regional managers).
// The global "All regions" schedule plus optional per-region overrides;
// regions with their own schedule are excluded from the global run.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Divider, FormControl, Grid, IconButton, InputLabel, MenuItem,
  Paper, Select, Switch, Tooltip, Typography
} from '@mui/material';
import { Delete as DeleteIcon, PlayArrow as RunIcon } from '@mui/icons-material';
import axios from 'axios';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

function PayoutScheduleSettings({ showNotification }) {
  const [schedules, setSchedules] = useState(null); // null = loading/not authorized
  const [regions, setRegions] = useState([]);
  const [newRegionId, setNewRegionId] = useState('');
  const [busy, setBusy] = useState(false);

  const notify = (message, severity = 'success') => {
    if (showNotification) showNotification(message, severity);
  };

  const load = useCallback(async () => {
    try {
      const [scheduleRes, regionRes] = await Promise.all([
        axios.get('/api/payout-schedules', { headers: getAuthHeader() }),
        axios.get('/api/regions', { headers: getAuthHeader() }).catch(() => ({ data: [] }))
      ]);
      setSchedules(scheduleRes.data);
      setRegions(Array.isArray(regionRes.data) ? regionRes.data : regionRes.data?.regions || []);
    } catch (err) {
      // 403 → not an owner/RM; hide the card entirely
      setSchedules(err.response?.status === 403 ? undefined : null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (schedules === undefined || schedules === null) return null;

  const save = async (schedule, changes) => {
    setBusy(true);
    try {
      await axios.put('/api/payout-schedules', {
        regionId: schedule.region?._id ?? null,
        dayOfWeek: changes.dayOfWeek ?? schedule.dayOfWeek,
        hour: changes.hour ?? schedule.hour,
        minute: changes.minute ?? schedule.minute,
        enabled: changes.enabled ?? schedule.enabled
      }, { headers: getAuthHeader() });
      await load();
      notify('Payout schedule saved');
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to save payout schedule', 'error');
    } finally {
      setBusy(false);
    }
  };

  const addOverride = async () => {
    if (!newRegionId) return;
    setBusy(true);
    try {
      await axios.put('/api/payout-schedules', {
        regionId: newRegionId, dayOfWeek: 0, hour: 6, minute: 0, enabled: true
      }, { headers: getAuthHeader() });
      setNewRegionId('');
      await load();
      notify('Region payout schedule added');
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to add region schedule', 'error');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (schedule) => {
    setBusy(true);
    try {
      await axios.delete(`/api/payout-schedules/${schedule._id}`, { headers: getAuthHeader() });
      await load();
      notify('Region schedule removed — the all-regions schedule covers it again');
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to remove schedule', 'error');
    } finally {
      setBusy(false);
    }
  };

  const runNow = async (schedule) => {
    if (!window.confirm(`Run the payout now for ${schedule.region ? schedule.region.name : 'ALL regions'}? This pays all eligible completed ACH jobs from the last full week.`)) return;
    setBusy(true);
    try {
      const res = await axios.post('/api/payout-schedules/run-now', {
        regionId: schedule.region?._id ?? null
      }, { headers: getAuthHeader() });
      notify(res.data.message);
    } catch (err) {
      notify(err.response?.data?.message || 'Manual payout run failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const regionsWithoutSchedule = regions.filter(
    (region) => !schedules.some((s) => s.region && s.region._id === region._id)
  );

  return (
    <Paper elevation={1} sx={{ p: 2, mt: 3 }}>
      <Typography variant="h6" gutterBottom>Automatic Payout Schedule</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        When the weekly ACH payout run happens. Regions with their own schedule are
        excluded from the all-regions run.
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {schedules.map((schedule) => (
        <Grid container spacing={1} alignItems="center" key={schedule._id} sx={{ mb: 1 }}>
          <Grid item xs={12} sm={3}>
            <Typography variant="body1">
              {schedule.region ? schedule.region.name : 'All regions'}
            </Typography>
          </Grid>
          <Grid item xs={4} sm={2.5}>
            <FormControl fullWidth size="small" disabled={!schedule.editable || busy}>
              <InputLabel>Day</InputLabel>
              <Select
                label="Day"
                value={schedule.dayOfWeek}
                onChange={(e) => save(schedule, { dayOfWeek: e.target.value })}
              >
                {DAYS.map((day, i) => <MenuItem key={day} value={i}>{day}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={3} sm={1.5}>
            <FormControl fullWidth size="small" disabled={!schedule.editable || busy}>
              <InputLabel>Hour</InputLabel>
              <Select
                label="Hour"
                value={schedule.hour}
                onChange={(e) => save(schedule, { hour: e.target.value })}
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <MenuItem key={h} value={h}>{String(h).padStart(2, '0')}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={3} sm={1.5}>
            <FormControl fullWidth size="small" disabled={!schedule.editable || busy}>
              <InputLabel>Min</InputLabel>
              <Select
                label="Min"
                value={schedule.minute}
                onChange={(e) => save(schedule, { minute: e.target.value })}
              >
                {[0, 15, 30, 45].map((m) => (
                  <MenuItem key={m} value={m}>{String(m).padStart(2, '0')}</MenuItem>
                ))}
                {![0, 15, 30, 45].includes(schedule.minute) && (
                  <MenuItem value={schedule.minute}>{String(schedule.minute).padStart(2, '0')}</MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3.5} sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title={schedule.enabled ? 'Enabled' : 'Disabled'}>
              <Switch
                checked={schedule.enabled}
                disabled={!schedule.editable || busy}
                onChange={(e) => save(schedule, { enabled: e.target.checked })}
              />
            </Tooltip>
            {schedule.editable && (
              <Tooltip title="Run this payout now">
                <IconButton onClick={() => runNow(schedule)} disabled={busy} color="primary">
                  <RunIcon />
                </IconButton>
              </Tooltip>
            )}
            {schedule.region && schedule.editable && (
              <Tooltip title="Remove region override">
                <IconButton onClick={() => remove(schedule)} disabled={busy} color="error">
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </Grid>
        </Grid>
      ))}

      {regionsWithoutSchedule.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 2 }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Add region-specific schedule</InputLabel>
            <Select
              label="Add region-specific schedule"
              value={newRegionId}
              onChange={(e) => setNewRegionId(e.target.value)}
            >
              {regionsWithoutSchedule.map((region) => (
                <MenuItem key={region._id} value={region._id}>{region.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={addOverride} disabled={!newRegionId || busy}>
            Add
          </Button>
        </Box>
      )}
    </Paper>
  );
}

export default PayoutScheduleSettings;
